/**
 * QuietHoursTimePicker - 24-hour format time picker for quiet hours
 * Provides hour and minute dropdowns for precise time selection
 */
import React from 'react';
import { Clock } from 'lucide-react';

interface QuietHoursTimePickerProps {
  label: string;
  value: string; // HH:MM format
  onChange: (value: string) => void;
  disabled?: boolean;
}

export const QuietHoursTimePicker: React.FC<QuietHoursTimePickerProps> = ({
  label,
  value,
  onChange,
  disabled = false
}) => {
  // Parse current value or use defaults
  const parseTime = (timeString: string): { hour: number; minute: number } => {
    if (!timeString || !timeString.includes(':')) {
      return { hour: 0, minute: 0 };
    }
    const [hourStr, minuteStr] = timeString.split(':');
    return {
      hour: parseInt(hourStr, 10) || 0,
      minute: parseInt(minuteStr, 10) || 0
    };
  };

  const { hour, minute } = parseTime(value);

  const handleHourChange = (newHour: number) => {
    const formattedTime = `${String(newHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    onChange(formattedTime);
  };

  const handleMinuteChange = (newMinute: number) => {
    const formattedTime = `${String(hour).padStart(2, '0')}:${String(newMinute).padStart(2, '0')}`;
    onChange(formattedTime);
  };

  // Generate hour options (0-23)
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  // Generate minute options (0, 15, 30, 45)
  const minutes = [0, 15, 30, 45];

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <div className="flex items-center gap-2 flex-1">
          {/* Hour Selector */}
          <select
            value={hour}
            onChange={(e) => handleHourChange(parseInt(e.target.value, 10))}
            disabled={disabled}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Hour"
          >
            {hours.map((h) => (
              <option key={h} value={h}>
                {String(h).padStart(2, '0')}
              </option>
            ))}
          </select>
          
          <span className="text-gray-500 dark:text-gray-400 font-bold">:</span>
          
          {/* Minute Selector */}
          <select
            value={minute}
            onChange={(e) => handleMinuteChange(parseInt(e.target.value, 10))}
            disabled={disabled}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Minute"
          >
            {minutes.map((m) => (
              <option key={m} value={m}>
                {String(m).padStart(2, '0')}
              </option>
            ))}
          </select>
        </div>
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {label === 'Start Time' ? 'When quiet hours begin' : 'When quiet hours end'} (24-hour format)
      </p>
    </div>
  );
};

export default QuietHoursTimePicker;
